import { samePalette } from 'fym-shared/src/music24/kitPalette';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { isAddedAsset } from '../../addedAssets/addedAssetFacts';

/** True when the asset was made in colours that the song does not use now. An old version (no stored colours) is not. */
export function hasOldColours(asset: SerializedKitAsset, palette: readonly string[] | undefined): boolean {
  return palette !== undefined && palette.length > 0 && asset.palette !== null && !samePalette(asset.palette, palette);
}

/** True when the asset was made in another theme than the song has now. */
export function hasOldTheme(asset: SerializedKitAsset, themeId: ReleaseThemeId): boolean {
  return asset.releaseThemeId !== themeId;
}

/**
 * The current assets of a song that were made in another theme or in other colours than the song
 * has now (spec §6.4; owner request 2026-10-06 for the colours). Only ready versions count: a
 * version in the making or a failed one gets the new look with its next attempt. Pure.
 */
export function staleKitAssets(
  assets: readonly SerializedKitAsset[],
  themeId: ReleaseThemeId,
  palette?: readonly string[]
): SerializedKitAsset[] {
  // A video the artist added has no theme, and the editor makes the music video: "Make them again"
  // never makes them (the server refuses an added version with 400 `M24_ADDED_NOT_REMADE`).
  return assets.filter(
    (asset) =>
      asset.status === 'ready' &&
      asset.supersededAt === null &&
      !isAddedAsset(asset) &&
      asset.themeId !== 'musicVideo' &&
      (hasOldTheme(asset, themeId) || hasOldColours(asset, palette))
  );
}

/** The one older theme of the stale assets, or null when they come from more than one theme. */
export function staleOldTheme(stale: readonly SerializedKitAsset[]): ReleaseThemeId | null {
  const themes = new Set(stale.map((asset) => asset.releaseThemeId));
  if (themes.size !== 1) {
    return null;
  }
  return stale[0]?.releaseThemeId ?? null;
}

/** True when every stale asset has the theme of the song and differs only in its colours. */
export function isColoursOnly(stale: readonly SerializedKitAsset[], themeId: ReleaseThemeId): boolean {
  return stale.length > 0 && stale.every((asset) => !hasOldTheme(asset, themeId));
}
